// streaming STT (spec: docs/superpowers/specs/2026-07-30-stt-provider-switch-design.md §3.4):
// OpenAI gpt-live-transcribe (Realtime API transcription session) WebSocket client。
// StreamingScribeClient を鏡写しにした構造で、プロトコルだけ OpenAI に置き換えた実装。
// 1 発話 = 1 commit。delta (差分) を item ごとに累積し、onPartial には累積全文を渡す
// (Scribe の「毎回全文」セマンティクスに揃え、呼び出し側はプロバイダを知らない)。
// 認証はサブプロトコル (URL にクエリなし)。session 設定 (model / languages / keywords /
// format / turn_detection: null) は client_secrets 発行時に埋め込み済みなので
// session.update は送らない (spec D6)。
// プロトコル対応表: docs/research/2026-07-30-openai-gpt-live-transcribe-survey.md §3
//
// R2 log expansion: event 型名は Scribe と共用 (wss.* / scribe.partial / scribe.final) し、
// この実装が emit する全イベントの payload に provider: "openai" を付ける (spec D5、無印 = elevenlabs)。

import { eventBus } from "../observability/eventBus";
import { makeEvent } from "../observability/eventSchema";
import { useChatStore } from "../state/chatStore";
import { upsample16kTo24k } from "../audio/resample";
import { floatToBase64Pcm16, truncateUtf8 } from "./streamingScribeClient";
import type { StreamingSttClient, StreamingSttClientOpts } from "./streamingSttClient";

// 診断ログは Scribe と同じ ?scribe-debug=1 を読む (プレフィックスで判別)。
// module-top const にせず関数内で判定する (テストが history.replaceState で差し替えられるように)。
function olog(...args: unknown[]): void {
  if (
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("scribe-debug") === "1"
  ) {
    console.info("[openai-stt]", ...args);
  }
}

type TokenResponse = { token: string; wsUrl: string; expiresAt: string };

// 判別フィールドは `type` (Scribe は `message_type`)。
// transcription_session.created / input_audio_buffer.committed / .speech_started 等の
// 付随イベントは default で無視する (server VAD は turn_detection: null で切ってある)。
type ServerMessage =
  | {
      type: "conversation.item.input_audio_transcription.delta";
      item_id?: string;
      delta?: string;
    }
  | {
      type: "conversation.item.input_audio_transcription.completed";
      item_id?: string;
      transcript?: string;
    }
  | {
      type: "conversation.item.input_audio_transcription.failed";
      item_id?: string;
      [k: string]: unknown;
    }
  | { type: "error"; [k: string]: unknown };

const TOKEN_ENDPOINT = "/api/stt/openai-token";
const PROVIDER = "openai";
const KEYTERM_MAX_LEN = 50;
/**
 * 1 通の `input_audio_buffer.append` に載せる最大サンプル数 (**24kHz 換算**) = 1 秒。
 * ElevenLabs 側 (`AUDIO_CHUNK_SAMPLES` = 16kHz の 1 秒) と**秒数を揃えてある** —
 * 送信側の都合ではなく「1 秒ぶんずつ送る」という同じ規則。
 *
 * **OpenAI 側の上限は実測していない** (既定が elevenlabs なので本番ログに現れない)。
 * ElevenLabs は 1 通 30 秒と確定させたが、これは provider ごとの値なので流用しない。
 * 1 秒に置くのは「どの provider でも安全な粒度」という判断で、
 * 実測するまでこれ以上大きくしない。
 */
const AUDIO_CHUNK_SAMPLES_24K = 24000;
const PARTIAL_THROTTLE_MS = 1000;
// Scribe と同じ 2 KB **UTF-8 bytes** cap (spec 2026-07-11 §5.2 / §6.3)
const TEXT_HARD_CAP_BYTES = 2048;
const WSS_ERROR_MSG_CAP = 500;

type WssErrorPhase = "token" | "connect" | "session" | "close";

export class StreamingOpenAiSttClient implements StreamingSttClient {
  private ws: WebSocket | null = null;
  private opts: StreamingSttClientOpts;

  private streamStartedAt: number | null = null;
  private lastPartialEmitMs = 0;
  private lastPartialText = "";
  private explicitCloseRequested = false;
  private currentTurnIndex = 0;
  private currentTurnId: string | undefined;
  private currentUtteranceIndex = 0;
  // commit() 呼び出し時刻 (= 発話終端の合図)。次の completed が届いた時点で差分を取り
  // scribe.final.latencyFromSpeechEndMs として emit し、その後 null に戻す
  // (1 utterance = 1 commit の前提。Scribe と同一セマンティクスでプロバイダ比較の主指標)。
  private lastCommitRequestedAt: number | null = null;
  // delta (差分) の累積バッファ。key は item_id (欠落時は "")。
  // completed で該当 item を破棄し、次の発話の partial が最初から始まるようにする。
  private deltaBuffers = new Map<string, string>();

  /**
   * 発話の終端 (completed / failed) で累積バッファを破棄する。
   *
   * item_id は**両イベントに載る保証が無い** (spec §6-6 / 調査ノート §5.2 — delta 側の正確な
   * ペイロードはドキュメントから確定できていない)。delta 側に item_id が無く completed 側にだけ
   * 在ると、delta はキー "" に積まれているのに completed は別キーを消すので**バッファが残留し、
   * 次の発話の partial に前の発話が連結される**。そのため終端では item_id のキーに加えて
   * 無キー ("") も必ず落とす。keyed な場合は "" が存在しないので no-op で、item ごとの分離は保たれる。
   */
  private discardBuffer(itemId: string): void {
    this.deltaBuffers.delete(itemId);
    this.deltaBuffers.delete("");
  }

  /**
   * 終端 (completed / failed) を受け取った item。次の `sendAudio` まで、この item の
   * delta は**確定済み発話の残響**として落とす (`sendAudio` で clear する)。
   *
   * ElevenLabs 側では「確定文の直後に同じテキストの partial が再送される」ことを preview 実機で
   * 実測した (PR #56)。その partial に対応する確定文はもう来ないので、確定文の到着でしか
   * 途中経過を消せない呼び出し側では消しようがなく、消したばかりの薄い吹き出しが復活して
   * 残り続ける。OpenAI 側は同じ形を**実測していない**が、構造上まったく同じ穴が開いている
   * (completed で累積を破棄した後の delta が、新しい累積として onPartial に流れる)。
   *
   * **窓を「次の sendAudio まで」に限る**のが要点で、広げると次の発話の live captions が
   * 二度と出ず voice-first の一次動線が無言になる。音声を送っていない以上、新しい発話の
   * delta は原理的に存在しないので、この窓の delta は残響と判定してよい。
   *
   * **判定は item 単位** (ElevenLabs 側の単一フラグと違う点)。複数 item が同時に in-flight に
   * なりうるかは未確定 (spec §6-6) で、一律に捨てるとまだ確定していない item の captions まで
   * 巻き添えで消える。無キー ("") を巻き込むのは `discardBuffer` と同じ理由 — delta 側に
   * item_id が無く completed 側にだけ在ると、delta は "" に積まれる一方で印は別キーに付き素通りする。
   */
  private completedItems = new Set<string>();

  private markCompleted(itemId: string): void {
    this.completedItems.add(itemId);
    this.completedItems.add("");
  }

  constructor(opts: StreamingSttClientOpts) {
    this.opts = opts;
  }

  /**
   * turn / utterance context を上書き。scribe.partial / scribe.final の payload に反映する
   * (StreamingScribeClient と同じ契約)。
   */
  setTurnContext(args: { turnIndex: number; turnId?: string; utteranceIndex: number }): void {
    this.currentTurnIndex = args.turnIndex;
    this.currentTurnId = args.turnId;
    this.currentUtteranceIndex = args.utteranceIndex;
  }

  private now(): number {
    return this.opts.now ? this.opts.now() : performance.now();
  }

  private elapsedMs(): number {
    return this.streamStartedAt === null ? 0 : this.now() - this.streamStartedAt;
  }

  async connect(keyterms: string[]): Promise<void> {
    // 前回 session 分の internal state を毎回 reset
    this.explicitCloseRequested = false;
    this.streamStartedAt = null;
    this.lastPartialEmitMs = 0;
    this.lastPartialText = "";
    this.lastCommitRequestedAt = null;
    this.deltaBuffers.clear();
    this.completedItems.clear();

    // keyterms の長さフィルタは Scribe と同じ基準をクライアント側で適用してから送る
    const filtered = keyterms.filter((s) => s.length > 0 && s.length <= KEYTERM_MAX_LEN);

    let tokenRes: Response;
    try {
      tokenRes = await fetch(TOKEN_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ keyterms: filtered }),
      });
    } catch (e) {
      const err = e as Error;
      this.emitWssError("token", {
        name: err?.name ?? "TokenFetchError",
        message: (err?.message ?? "").slice(0, WSS_ERROR_MSG_CAP),
      });
      throw e;
    }
    if (!tokenRes.ok) {
      this.emitWssError("token", {
        httpStatus: tokenRes.status,
        name: "TokenFetchFailed",
        message: `token fetch failed: ${tokenRes.status}`.slice(0, WSS_ERROR_MSG_CAP),
      });
      throw new Error(`token fetch failed: ${tokenRes.status}`);
    }
    const { token, wsUrl } = (await tokenRes.json()) as TokenResponse;

    return new Promise((resolve, reject) => {
      // ブラウザ WS の認証はサブプロトコルで渡す (URL にクエリなし)
      const ws = new WebSocket(wsUrl, ["realtime", "openai-insecure-api-key." + token]);
      this.ws = ws;
      ws.onopen = () => {
        this.streamStartedAt = this.now();
        this.emitEvent("wss.connected", { tsMs: 0 });
        olog("ws open", { keyterms: filtered.length });
        resolve();
      };
      ws.onerror = (e) => {
        const err = e as unknown as { message?: string };
        this.emitWssError("connect", {
          name: "WebSocketError",
          message: (err?.message ?? "").slice(0, WSS_ERROR_MSG_CAP),
        });
        this.opts.onError?.(e);
        reject(new Error("WS error"));
      };
      ws.onclose = (ev: CloseEvent) => {
        const tsMs = this.elapsedMs();
        olog("ws CLOSED", {
          code: ev.code,
          reason: ev.reason,
          wasClean: ev.wasClean,
          elapsedMs: Math.round(tsMs),
          initiator: this.explicitCloseRequested ? "client" : "server",
        });
        this.emitEvent("wss.disconnected", {
          tsMs,
          code: ev.code,
          reason: ev.reason,
          wasClean: ev.wasClean,
          initiator: this.explicitCloseRequested ? "client" : "server",
        });
        // 理由が付いた切断は clean でも異常として記録する (ElevenLabs 側と同じ判断)。
        // あちらは `chunk_size_exceeded` が `code=1000 / wasClean=true` で届き、
        // 従来の判定では**正常な切断として素通りしていた**。OpenAI 側で同種の拒否が
        // どう届くかは未実測だが、**素通りさせない側に倒す**のが安全
        // (誤検知の代償は「ログが 1 行増える」だけ、見逃しの代償は「回答が消えた
        // 原因が永久に分からない」)。
        const rejected = ev.reason !== "";
        if (rejected || !ev.wasClean || ev.code !== 1000) {
          this.emitWssError("close", {
            name: rejected ? "ServerRejected" : "AbnormalClose",
            message: `code=${ev.code} reason=${ev.reason}`.slice(0, WSS_ERROR_MSG_CAP),
          });
        }
      };
      ws.onmessage = (ev) => {
        const evData = typeof ev.data === "string" ? ev.data : "";
        let msg: ServerMessage;
        try {
          msg = JSON.parse(evData) as ServerMessage;
        } catch {
          return;
        }
        switch (msg.type) {
          case "conversation.item.input_audio_transcription.delta": {
            const itemId = msg.item_id ?? "";
            // 確定済み item の残響 (上の completedItems を参照)。通すと呼び出し側の
            // live captions が復活し、対応する確定文が来ないまま残る。
            if (this.completedItems.has(itemId)) break;
            const accumulated = (this.deltaBuffers.get(itemId) ?? "") + (msg.delta ?? "");
            this.deltaBuffers.set(itemId, accumulated);
            const now = this.now();
            const changed = accumulated !== this.lastPartialText;
            const elapsed = now - this.lastPartialEmitMs;
            if (changed || elapsed >= PARTIAL_THROTTLE_MS) {
              const { text, truncated } = truncateUtf8(accumulated, TEXT_HARD_CAP_BYTES);
              this.emitEvent("scribe.partial", {
                tsMs: this.elapsedMs(),
                utteranceIndex: this.currentUtteranceIndex,
                turnIndex: this.currentTurnIndex,
                text,
                charCount: accumulated.length,
                ...(truncated ? { _truncated: true } : {}),
              });
              this.lastPartialEmitMs = now;
              this.lastPartialText = accumulated;
            }
            olog("delta:", accumulated);
            this.opts.onPartial(accumulated);
            break;
          }
          case "conversation.item.input_audio_transcription.completed": {
            const itemId = msg.item_id ?? "";
            const transcript = msg.transcript ?? "";
            const { text, truncated } = truncateUtf8(transcript, TEXT_HARD_CAP_BYTES);
            // commit() 呼び出し (= 発話終端) から今回の確定までの latency。
            // commit() を経由していない completed は undefined のまま payload に含めない。
            const latencyFromSpeechEndMs =
              this.lastCommitRequestedAt !== null
                ? this.now() - this.lastCommitRequestedAt
                : undefined;
            this.lastCommitRequestedAt = null;
            // **markCompleted の前に読む。** 判定基準は「`sendAudio` を挟んでいないこと」で
            // あって item_id ではない (同じ発話でも item は分かれうるし、item_id が同じでも
            // `sendAudio` を挟めば別の発話)。ElevenLabs 側の committedSinceLastAudio と
            // 同じ意味 — completedItems は sendAudio (:334 相当) で clear される。
            const continuation = this.completedItems.size > 0;
            this.emitEvent("scribe.final", {
              tsMs: this.elapsedMs(),
              utteranceIndex: this.currentUtteranceIndex,
              turnIndex: this.currentTurnIndex,
              text,
              charCount: transcript.length,
              ...(truncated ? { _truncated: true } : {}),
              ...(latencyFromSpeechEndMs !== undefined ? { latencyFromSpeechEndMs } : {}),
              // 割れた 2 件目であることを R2 に残す (ElevenLabs 側と同形)
              ...(continuation ? { continuation: true } : {}),
            });
            // 該当 item の累積を破棄 (次の発話の partial が最初から始まる)
            this.discardBuffer(itemId);
            this.markCompleted(itemId);
            olog("completed:", transcript, continuation ? "(continuation)" : "");
            this.opts.onCommitted(transcript, { continuation });
            break;
          }
          case "conversation.item.input_audio_transcription.failed": {
            // commit 済み item の文字起こし失敗 (error オブジェクト付き)。"error" と同じく
            // onError に bubble し、呼び出し側の stt-ws-failed → 原則 5 の text fallback に落とす。
            // completed 相当の終端でもあるため、latency 起点と該当 item の累積を破棄する
            // (残すと次の completed に失敗した commit 起点の latency が付き、累積に前の発話が混入する)。
            this.lastCommitRequestedAt = null;
            this.discardBuffer(msg.item_id ?? "");
            this.markCompleted(msg.item_id ?? "");
            this.emitWssError("session", {
              messageType: msg.type,
              name: msg.type,
              message: JSON.stringify(msg).slice(0, WSS_ERROR_MSG_CAP),
            });
            olog("transcription failed:", msg);
            this.opts.onError?.(msg);
            break;
          }
          case "error":
            this.emitWssError("session", {
              messageType: msg.type,
              name: msg.type,
              message: JSON.stringify(msg).slice(0, WSS_ERROR_MSG_CAP),
            });
            this.opts.onError?.(msg);
            break;
          default:
            // 無視 (transcription_session.created / input_audio_buffer.committed /
            // .speech_started / .speech_stopped / 未知 type)
            break;
        }
      };
    });
  }

  sendAudio(pcm: Float32Array): void {
    if (!this.ws || this.ws.readyState !== 1) {
      olog("sendAudio DROPPED (ws not open)", {
        readyState: this.ws?.readyState ?? "null",
        samples: pcm.length,
      });
      return;
    }
    olog("sendAudio", { samples: pcm.length });
    // 新しい発話が始まった。ここから先の delta は残響ではないので通す。
    this.completedItems.clear();
    // VAD の 16kHz mono を 24kHz へアップサンプルしてから送る (spec D4。
    // サンプルレート変換はこの実装の内部責務で、呼び出し側は 16kHz のまま)。
    //
    // **アップサンプルは全体に対して 1 度だけ行い、その後で分割する。** 逆順
    // (分割 → チャンクごとにアップサンプル) にすると、`upsample16kTo24k` の線形補間が
    // 各チャンクの末尾で「次チャンクの先頭」ではなく自分の最終サンプルへ clamp するため、
    // 境界ごとに不連続が入る。
    //
    // 分割する理由は ElevenLabs 側と同じ (2026-08-11 実測の `chunk_size_exceeded`)。
    // **こちらは 24kHz へ引き伸ばすので 1.5 倍大きく**、37 秒の発話が約 2.37 MB になる。
    // OpenAI 側の上限は未実測だが、1 通に詰め込むのは provider に関わらず誤り。
    // チケット: docs/tickets/2026-08-11-stt-chunk-size-exceeded.md
    const out = upsample16kTo24k(pcm);
    for (let i = 0; i < out.length; i += AUDIO_CHUNK_SAMPLES_24K) {
      // 送信中の切断は現実に起きる (アイドル切断)。throw させると onSpeechEnd が
      // 途中で止まり commit ごと落ちるので、毎回見て打ち切る。
      if (this.ws.readyState !== 1) {
        olog("sendAudio TRUNCATED (ws closed mid-send)", { sent: i, total: out.length });
        return;
      }
      this.ws.send(
        JSON.stringify({
          type: "input_audio_buffer.append",
          audio: floatToBase64Pcm16(out.subarray(i, i + AUDIO_CHUNK_SAMPLES_24K)),
        })
      );
    }
  }

  commit(): void {
    if (!this.ws || this.ws.readyState !== 1) {
      olog("commit DROPPED (ws not open)", { readyState: this.ws?.readyState ?? "null" });
      return;
    }
    olog("commit");
    // 発話終端 timestamp を記録 (scribe.final.latencyFromSpeechEndMs の起点)
    this.lastCommitRequestedAt = this.now();
    // クライアント側 Silero VAD が発話境界を決める manual commit (turn_detection: null)
    this.ws.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
  }

  close(): void {
    // ws.close() の同期発火 onclose 内で initiator=client 判定できるよう
    // 先に flag を立ててから WebSocket を close する (順序が load-bearing)
    this.explicitCloseRequested = true;
    this.ws?.close();
    this.ws = null;
  }

  /**
   * WS が送信可能 (OPEN) かどうか。continuous mode の長寿命 WS は無音中に server 側で
   * アイドル切断されうるため、発話送信前に開通確認して張り直すのに使う
   * (ensureScribeConnected の開通保証がインターフェース経由でそのまま効く)。
   */
  isOpen(): boolean {
    return this.ws !== null && this.ws.readyState === 1;
  }

  /**
   * opts.sessionId 優先、未指定なら useChatStore.getState().sessionId にフォールバック
   * (StreamingScribeClient と同じ resolution パターン。useContinuousMode は sessionId を
   * 渡さずに構築するため、これが無いと production で全 emit が dead code になる)。
   */
  private resolveSessionId(): string | null {
    return this.opts.sessionId ?? useChatStore.getState().sessionId;
  }

  private emitEvent(type: string, payload: Record<string, unknown>): void {
    const sessionId = this.resolveSessionId();
    if (!sessionId) return;
    eventBus.emit(
      makeEvent(type, sessionId, { provider: PROVIDER, ...payload }, this.currentTurnId)
    );
  }

  private emitWssError(
    phase: WssErrorPhase,
    detail: { name: string; message: string; httpStatus?: number; messageType?: string }
  ): void {
    const sessionId = this.resolveSessionId();
    if (!sessionId) return;
    eventBus.emit(
      makeEvent(
        "wss.error",
        sessionId,
        { provider: PROVIDER, phase, ...detail },
        this.currentTurnId
      )
    );
  }
}
